import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input, DateOverlay, dateTextHiddenCls } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  ArrowLeft, Save, CheckCircle, XCircle, MinusCircle,
  ChevronDown, ChevronUp, FileSignature, Clock, Zap, Wrench, AlertTriangle, RotateCcw, Download, Printer, Lock, Camera, CloudOff
} from 'lucide-react';
import AuditTrailPanel from './AuditTrailPanel';
import FloatingActions from '../FloatingActions';
import EntityPhotoGallery from '../EntityPhotoGallery';
import {
  enqueue as enqueueOffline, cacheChecklist, isLocalId,
} from '../../lib/offlineQueue';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import SignaturePad from '../ui/SignaturePad';
import { captureSignTime, formatSignedLocal, formatSignedUtc } from '../../lib/signTime';
import useAutosave, { AUTOSAVE_LABEL } from '../../hooks/useAutosave';
import { useDefaultSignature } from '../../hooks/useDefaultSignature';
import { format } from 'date-fns';
import { toast } from 'sonner';
import LazyAuthImage from '../shared/LazyAuthImage';
import ScrollX from '../shared/ScrollX';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_BUTTONS = [
  { value: 'pass', label: i18n.t('auto.lbl.pass'), icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' },
  { value: 'fail', label: i18n.t('auto.lbl.fail'), icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' },
  { value: 'na', label: 'N/A', icon: MinusCircle, color: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200' },
];

const STATUS_CONFIG = {
  draft: { label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: i18n.t('auto.lbl.inProgress'), color: 'bg-blue-100 text-blue-700' },
  completed: { label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-700' },
  conditional_approval: { label: i18n.t('auto.lbl.conditionalApproval'), color: 'bg-orange-100 text-orange-700' },
  signed_off: { label: i18n.t('auto.lbl.signedOff'), color: 'bg-green-100 text-green-700' },
};

// Plant details on a checklist header are the same facts the register
// already holds. Match header fields by key *or* label (templates generate
// keys from labels, so both spellings turn up) and fill them from the asset.
const ASSET_HEADER_MAP = [
  { test: /(make|manufacturer|producent)/i, pick: (a) => a.manufacturer },
  { test: /(model|typ)/i, pick: (a) => a.model_name },
  { test: /(serial|nr.?seryjn)/i, pick: (a) => a.serial_number },
  { test: /(year|rok)/i, pick: (a) => a.year_of_manufacture },
  { test: /(registration|reg.?no|rejestr)/i, pick: (a) => a.registration_number },
  { test: /(machine|asset|plant|equipment|fleet|inventory).*(no|number|id|ref)|^(machine|asset|plant)_?id/i,
    pick: (a) => a.inventory_number },
  { test: /(machine|equipment|plant).*(name|description|type)/i, pick: (a) => a.name },
];

const assetHeaderValues = (asset, headerFields = []) => {
  const out = {};
  for (const f of headerFields) {
    const hay = `${f.key || ''} ${f.label || ''}`;
    const rule = ASSET_HEADER_MAP.find((r) => r.test.test(hay));
    if (!rule) continue;
    const v = rule.pick(asset);
    if (v !== null && v !== undefined && String(v) !== '') out[f.key] = String(v);
  }
  return out;
};

const ChecklistFillForm = ({ instance, template: templateProp, token, projects, employees, currentUser, onBack, onExportPDF }) => {
  const { t } = useTranslation();
  const { savedSignature, saveAsDefault } = useDefaultSignature();
  const [template, setTemplate] = useState(templateProp || null);
  const [sectionsData, setSectionsData] = useState(instance?.sections_data || []);
  const [headerData, setHeaderData] = useState(instance?.header_data || {});
  const [signOffs, setSignOffs] = useState(instance?.sign_offs || []);
  const [projectId, setProjectId] = useState(instance?.project_id || '');
  const [assetId, setAssetId] = useState(instance?.asset_id || '');
  const [assetOptions, setAssetOptions] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [notes, setNotes] = useState(instance?.notes || '');
  const [currentStatus, setCurrentStatus] = useState(instance?.status || 'draft');
  const [reloadHistory, setReloadHistory] = useState(0);

  // Fetch template if not provided
  useEffect(() => {
    if (!template && instance?.template_id) {
      fetch(`${API_BASE}/api/checklists/templates/${instance.template_id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(setTemplate)
        .catch(console.error);
    }
  }, [template, instance, token]);

  // Equipment picker options. Served from the checklists namespace so a
  // fitter can name the forklift without inventory access. Kit assigned to
  // the linked project is listed first.
  useEffect(() => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    fetch(`${API_BASE}/api/checklists/asset-options${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setAssetOptions(Array.isArray(d) ? d : []))
      .catch(() => setAssetOptions([]));
  }, [token, projectId]);

  // Expand all sections by default
  useEffect(() => {
    if (template?.sections) {
      const expanded = {};
      template.sections.forEach((_, i) => { expanded[i] = true; });
      setExpandedSections(expanded);
    }
  }, [template]);

  // Picking the machine fills its own details — make, model, serial, fleet
  // number — straight off the register instead of being copied by hand.
  const selectedAsset = assetOptions.find((a) => a.id === assetId) || null;
  const handleAssetChange = (newAssetId) => {
    setAssetId(newAssetId);
    if (!newAssetId) return;
    const asset = assetOptions.find((a) => a.id === newAssetId);
    if (!asset) return;
    const filled = assetHeaderValues(asset, template?.header_fields || []);
    if (Object.keys(filled).length) {
      setHeaderData(prev => ({ ...prev, ...filled }));
      toast.success(`Plant details filled from ${asset.inventory_number || asset.name}`);
    }
  };

  // Auto-fill header when project changes
  const handleProjectChange = async (newProjectId) => {    const oldProjectId = projectId;
    setProjectId(newProjectId);
    if (!newProjectId) return;

    try {
      const projResp = await fetch(`${API_BASE}/api/projects/${newProjectId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!projResp.ok) return;
      const project = await projResp.json();
      const discipline = instance?.discipline || template?.discipline || '';

      const updated = { ...headerData };

      // Project name
      updated.project_name = project.name || '';

      // Client from customer
      if (project.customer_id) {
        try {
          const custResp = await fetch(`${API_BASE}/api/customers/${project.customer_id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (custResp.ok) {
            const customer = await custResp.json();
            updated.client = customer.name || customer.company_name || '';
          }
        } catch {}
      }

      // Supervisor from project contacts, fallback to team roles
      const supervisorKey = discipline === 'electrical' ? 'electrical_supervisor' : 'mechanical_supervisor';
      const supervisorContact = project[supervisorKey];
      let supervisorName = supervisorContact?.name?.trim() || '';

      if (!supervisorName) {
        const fallbackRole = discipline === 'electrical' ? t('auto.iso.elTeamLeader') : t('auto.iso.meTeamLeader');
        const teamMember = (project.team_members || []).find(m => m.role === fallbackRole);
        if (teamMember?.employee_id) {
          try {
            const empResp = await fetch(`${API_BASE}/api/employees/${teamMember.employee_id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (empResp.ok) {
              const emp = await empResp.json();
              supervisorName = emp.name || '';
            }
          } catch {}
        }
      }
      if (supervisorName) {
        updated[supervisorKey] = supervisorName;
      }

      setHeaderData(updated);
    } catch (e) {
      console.error('Auto-fill error:', e);
    }
  };

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // When items change on an approved checklist, revert status to in_progress
  const revertIfApproved = () => {
    if (currentStatus === 'completed' || currentStatus === 'conditional_approval') {
      setCurrentStatus('in_progress');
    }
  };

  const updateItem = (sectionIdx, itemIdx, field, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) {
        newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      }
      const section = newData[sectionIdx];
      let item = section.items.find(i => i.item_index === itemIdx);
      if (!item) {
        item = { item_index: itemIdx, status: null, value: null, notes: null };
        section.items.push(item);
      }
      // Toggle: clicking the same status clears it
      if (field === 'status' && item.status === value) {
        item.status = null;
      } else {
        item[field] = value;
      }
      return newData;
    });
    if (field === 'status') revertIfApproved();
  };

  const getItemData = (sectionIdx, itemIdx) => {
    const section = sectionsData[sectionIdx];
    if (!section) return { status: null, value: null, notes: null };
    const item = section.items?.find(i => i.item_index === itemIdx);
    return item || { status: null, value: null, notes: null };
  };

  // Weekly-grid support: each item stores a nested ``days`` map
  // ``{MON:"pass"|"fail"|"na"|null, TUE:…}`` so one row spans the
  // whole week. Same underlying `sections_data` shape as legacy
  // items so it round-trips cleanly through the backend.
  const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // Derive 1-3 initials from the logged-in user's full name — falls
  // back to the first two letters of their username.
  const userInitials = React.useMemo(() => {
    const full = (currentUser?.full_name || '').trim();
    if (full) {
      const parts = full.split(/\s+/).filter(Boolean);
      const chars = parts.slice(0, 3).map(p => p[0]).join('');
      if (chars) return chars.toUpperCase();
    }
    const uname = (currentUser?.username || '').trim();
    return uname ? uname.substring(0, 3).toUpperCase() : '';
  }, [currentUser]);

  // Find the first signature-type item in a section and, if that day's
  // initials slot is empty, pre-fill it with the logged-in user's
  // initials. Mutates ``secData`` in-place (called inside a set()).
  const autofillInitialsForDay = (secData, tplSection, day) => {
    if (!userInitials) return;
    const sigIdx = (tplSection.items || []).findIndex(
      (it) => (it?.type || '').toLowerCase() === 'signature'
    );
    if (sigIdx < 0) return;
    let sigItem = secData.items.find(i => i.item_index === sigIdx);
    if (!sigItem) { sigItem = { item_index: sigIdx, initials: {} }; secData.items.push(sigItem); }
    if (!sigItem.initials) sigItem.initials = {};
    if (!sigItem.initials[day]) sigItem.initials[day] = userInitials;
  };

  const updateItemDay = (sectionIdx, itemIdx, day, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      const section = newData[sectionIdx];
      let item = section.items.find(i => i.item_index === itemIdx);
      if (!item) { item = { item_index: itemIdx, days: {} }; section.items.push(item); }
      if (!item.days) item.days = {};
      // Toggle behaviour: clicking the same value clears the cell.
      const wasEmpty = !item.days[day];
      item.days[day] = item.days[day] === value ? null : value;
      // Auto-fill inspector initials on the first tick of that day.
      if (wasEmpty && item.days[day]) {
        const tplSection = template?.sections?.[sectionIdx];
        if (tplSection) autofillInitialsForDay(section, tplSection, day);
      }
      return newData;
    });
  };
  const getItemDay = (sectionIdx, itemIdx, day) => {
    const it = getItemData(sectionIdx, itemIdx);
    return (it.days && it.days[day]) || null;
  };

  // Signature-row helpers — one initials text field per weekday.
  const updateItemInitials = (sectionIdx, itemIdx, day, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      const section = newData[sectionIdx];
      let item = section.items.find(i => i.item_index === itemIdx);
      if (!item) { item = { item_index: itemIdx, initials: {} }; section.items.push(item); }
      if (!item.initials) item.initials = {};
      item.initials[day] = value;
      return newData;
    });
  };
  const getItemInitial = (sectionIdx, itemIdx, day) => {
    const it = getItemData(sectionIdx, itemIdx);
    return (it.initials && it.initials[day]) || '';
  };

  // ── Defect log helpers ───────────────────────────────────────────
  // Rows persist under ``sections_data[sIdx].defects`` as an array
  // of ``{date, item_ref, defect, risk, action, closed_by,
  // closed_date}``. Blank rows are kept so the operator can pen-fill
  // the printed sheet.
  const getDefects = (sectionIdx) => {
    return (sectionsData[sectionIdx]?.defects) || [];
  };
  const updateDefect = (sectionIdx, rowIdx, field, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [], defects: [] };
      if (!newData[sectionIdx].defects) newData[sectionIdx].defects = [];
      while (newData[sectionIdx].defects.length <= rowIdx) newData[sectionIdx].defects.push({});
      newData[sectionIdx].defects[rowIdx][field] = value;
      return newData;
    });
  };
  const addDefectRow = (sectionIdx) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [], defects: [] };
      if (!newData[sectionIdx].defects) newData[sectionIdx].defects = [];
      newData[sectionIdx].defects.push({});
      return newData;
    });
  };
  const removeDefectRow = (sectionIdx, rowIdx) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]?.defects) return prev;
      newData[sectionIdx].defects.splice(rowIdx, 1);
      return newData;
    });
  };

  // ── Equipment-status helpers ─────────────────────────────────────
  // Single-choice radio + optional free-text note. Persisted under
  // ``sections_data[sIdx].equipment_status`` / ``.note``.
  const getEquipmentStatus = (sectionIdx) => {
    return (sectionsData[sectionIdx]?.equipment_status) || '';
  };
  const getEquipmentNote = (sectionIdx) => {
    return (sectionsData[sectionIdx]?.note) || '';
  };
  const setEquipmentStatus = (sectionIdx, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      newData[sectionIdx].equipment_status = value;
      return newData;
    });
  };
  const setEquipmentNote = (sectionIdx, value) => {
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      newData[sectionIdx].note = value;
      return newData;
    });
  };

  // Bulk-set a whole (day, value) column in a weekly-grid section —
  // triggered by clicking the day's P / F / N/A column header. The
  // common workflow is "everything passed except item 7" so people
  // hit "P" for the day, then override the single failure. Skips
  // items that already hold a *different* explicit value so the
  // click doesn't clobber deliberate edits — click again to force.
  // Subheader / signature rows are never touched.
  const bulkSetDay = (sectionIdx, day, value) => {
    const section = template?.sections?.[sectionIdx];
    if (!section) return;
    const tickable = (it) => {
      const t = (it?.type || 'check').toLowerCase();
      return t !== 'subheader' && t !== 'signature';
    };
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      const secData = newData[sectionIdx];
      // Are we already all-set to this value for this day? Then the
      // click *clears* it (toggle behaviour).
      const currentDays = (section.items || []).map((_it, iIdx) => {
        if (!tickable(_it)) return value;  // ignored in "allSame" check
        const it = secData.items.find(i => i.item_index === iIdx);
        return it?.days?.[day] || null;
      });
      const allSame = currentDays.length > 0 && currentDays.every(v => v === value);
      const nextVal = allSame ? null : value;
      (section.items || []).forEach((_item, iIdx) => {
        if (!tickable(_item)) return;
        let it = secData.items.find(i => i.item_index === iIdx);
        if (!it) { it = { item_index: iIdx, days: {} }; secData.items.push(it); }
        if (!it.days) it.days = {};
        it.days[day] = nextVal;
      });
      // Auto-fill inspector initials for this day if a signature row
      // exists and its slot is empty.
      autofillInitialsForDay(secData, section, day);
      return newData;
    });
  };

  // Bulk set all items in a section to a status (only for check/test items)
  const setSectionStatus = (sectionIdx, status) => {
    if (!template?.sections?.[sectionIdx]) return;
    const tplSection = template.sections[sectionIdx];
    setSectionsData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (!newData[sectionIdx]) {
        newData[sectionIdx] = { section_index: sectionIdx, items: [] };
      }
      tplSection.items.forEach((tplItem, iIdx) => {
        const tt = (tplItem.type || 'check').toLowerCase();
        if (tt === 'text' || tt === 'subheader' || tt === 'signature') return; // skip non-tickable rows
        let item = newData[sectionIdx].items.find(i => i.item_index === iIdx);
        if (!item) {
          item = { item_index: iIdx, status: null, value: null, notes: null };
          newData[sectionIdx].items.push(item);
        }
        item.status = status;
      });
      return newData;
    });
    revertIfApproved();
  };

  // Check if all check/test items are pass or na (for approval validation)
  const getApprovalStatus = () => {
    if (!template?.sections) return { canApprove: false, hasFails: false, allFilled: false };
    let total = 0, filled = 0, fails = 0;
    template.sections.forEach((section, sIdx) => {
      // Defect log & equipment status don't have per-item ticks so
      // they can't block approval. If Equipment Status is explicitly
      // set to "removed", flag it as a fail so the checklist can't
      // silently pass while the machine is tagged out.
      if (section.type === 'defect_log') return;
      if (section.type === 'equipment_status') {
        const val = sectionsData[sIdx]?.equipment_status;
        if (val === 'removed') fails++;
        return;
      }
      section.items?.forEach((item, iIdx) => {
        const tt = (item.type || 'check').toLowerCase();
        if (tt === 'text' || tt === 'subheader' || tt === 'signature') return;
        // Weekly-grid items are considered "filled" if any weekday has
        // a value — otherwise counting each of 7 days would inflate
        // the total in mixed templates.
        if (section.type === 'weekly_grid') {
          total++;
          const data = getItemData(sIdx, iIdx);
          const anyDay = Object.values(data.days || {}).some(v => v);
          const anyFail = Object.values(data.days || {}).includes('fail');
          if (anyDay) filled++;
          if (anyFail) fails++;
          return;
        }
        total++;
        const data = getItemData(sIdx, iIdx);
        if (data.status) filled++;
        if (data.status === 'fail') fails++;
      });
    });
    const allFilled = total > 0 && filled === total;
    return { canApprove: allFilled && fails === 0, hasFails: fails > 0, allFilled };
  };

  const handleSave = async (newStatus, { silent = false } = {}) => {
    if (savingRef.current) return;
    savingRef.current = true;
    if (!silent) setIsSaving(true);
    const body = {
      header_data: headerData,
      sections_data: sectionsData,
      // `pending_sync` is a device-only marker for an offline sign-off —
      // never let it reach the server.
      sign_offs: signOffs.map(({ pending_sync, ...s }) => s),
      project_id: projectId || null,
      asset_id: assetId || '',
      notes: notes || null,
    };
    if (newStatus) body.status = newStatus;

    // Offline (or created offline and not yet synced): keep it on the
    // device and queue one write per checklist. Last save wins.
    const saveLocally = async () => {
      const pendingCreate = isLocalId(instance.id);
      await cacheChecklist(
        { ...instance, ...body, status: newStatus || currentStatus },
        template,
      );
      await enqueueOffline({
        url: pendingCreate
          ? `${API_BASE}/api/checklists/instances`
          : `${API_BASE}/api/checklists/instances/${instance.id}`,
        method: pendingCreate ? t('auto.iso.post') : t('auto.iso.put'),
        body: pendingCreate
          ? { template_id: template?.id, project_id: projectId || null, ...body }
          : { ...body, base_updated_at: instance.updated_at || instance.created_at || null },
        token,
        dedupeKey: `checklist:${instance.id}`,
        localChecklistId: pendingCreate ? instance.id : null,
        label: `Checklist ${instance.checklist_number || ''}`.trim(),
      });
      if (newStatus) setCurrentStatus(newStatus);
      if (!silent) {
        toast.success('Saved on this device — it will sync when you\'re back online');
      }
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await saveLocally();
        return;
      }

      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instance.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      if (newStatus) setCurrentStatus(newStatus);
      // Refresh the on-device copy so it stays usable if signal drops.
      cacheChecklist({ ...instance, ...body, status: newStatus || currentStatus }, template)
        .catch(() => { /* storage full / private mode — not fatal */ });
      if (!silent) {
        toast.success(newStatus ? `Status: ${newStatus.replace(/_/g, ' ')}` : 'Progress saved');
        setReloadHistory(k => k + 1);
      }
    } catch (e) {
      // A fetch that throws means the network died mid-save, not that the
      // server rejected us — fall back to the device rather than lose work.
      const networkDown = e instanceof TypeError || e.message === 'Failed to fetch';
      if (networkDown) {
        try {
          await saveLocally();
          return;
        } catch { /* fall through to the error below */ }
      }
      if (silent) throw e;
      toast.error(e.message);
    }
    finally {
      savingRef.current = false;
      if (!silent) setIsSaving(false);
    }
  };

  const handleSignOff = async (roleIdx) => {
    const role = signOffs[roleIdx];
    const r = String(role?.role || '').toLowerCase();
    const isClient = r.includes('client') || r.includes('customer');
    const fallbackName = !isClient ? (currentUser?.full_name || currentUser?.username || '') : '';
    const effectiveName = (role?.name && role.name.trim()) || fallbackName.trim();
    if (!effectiveName) { toast.error(t('auto.iso.enterANameBeforeSigning')); return; }
    const signatureImage = role?.signature_image || null;
    const stamp = captureSignTime();

    // No signal — or the sheet itself hasn't reached the server yet. Sign
    // on the device and queue the sign-off behind the create.
    const signLocally = async () => {
      const nextSignOffs = signOffs.map((s, i) => (
        i === roleIdx
          ? {
            ...s, name: effectiveName, signature_image: signatureImage,
            signed: true, signed_at: new Date().toISOString(), ...stamp,
            pending_sync: true,
          }
          : s
      ));
      setSignOffs(nextSignOffs);
      await enqueueOffline({
        url: `${API_BASE}/api/checklists/instances/${instance.id}/sign-off`,
        method: 'POST',
        body: { role: role.role, name: effectiveName, signature_image: signatureImage, ...stamp },
        token,
        dedupeKey: `signoff:${instance.id}:${role.role}`,
        label: `Sign-off (${role.role}) — ${instance.checklist_number || 'checklist'}`,
      });
      await cacheChecklist(
        {
          ...instance,
          header_data: headerData,
          sections_data: sectionsData,
          notes: notes || null,
          sign_offs: nextSignOffs,
          status: currentStatus,
        },
        template,
      ).catch(() => { /* storage unavailable */ });
      if (nextSignOffs.every(s => s.signed)) setCurrentStatus('signed_off');
      toast.success('Signed on this device — it will upload when you\'re back online');
    };

    try {
      const noServerCopy = isLocalId(instance.id);
      if ((typeof navigator !== 'undefined' && navigator.onLine === false) || noServerCopy) {
        await signLocally();
        return;
      }
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instance.id}/sign-off`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ role: role.role, name: effectiveName, signature_image: signatureImage, ...stamp }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Sign-off failed');
      }
      const data = await resp.json();
      // Update local state
      setSignOffs(prev => prev.map((s, i) => i === roleIdx ? { ...s, name: effectiveName, signature_image: signatureImage, signed: true, ...stamp } : s));
      toast.success(`Signed: ${role.role}`);
      setReloadHistory(k => k + 1);
      if (data.all_signed) {
        setCurrentStatus('signed_off');
        toast.success(t('auto.iso.allRolesSignedChecklistSignedOff'));
      }
    } catch (e) {
      // A throwing fetch means the network died, not that we were
      // refused — keep the signature rather than lose it.
      const networkDown = e instanceof TypeError || e.message === 'Failed to fetch';
      if (networkDown) {
        try { await signLocally(); return; } catch { /* fall through */ }
      }
      toast.error(e.message);
    }
  };

  // Update the in-progress signature data URL for a specific role
  // without touching any other state so the pad stays responsive.
  const updateSignatureImage = (roleIdx, dataUrl) => {
    setSignOffs(prev => prev.map((s, i) => i === roleIdx ? { ...s, signature_image: dataUrl } : s));
  };

  // Default signatory name: pre-fill internal roles with current user's full
  // name; Client/Customer roles must be typed from scratch.
  const defaultSignName = (role) => {
    const r = String(role || '').toLowerCase();
    if (r.includes('client') || r.includes('customer')) return '';
    return currentUser?.full_name || currentUser?.username || '';
  };

  // Revert to draft / re-open: admins and the document creator (per
  // product decision, the creator may re-open any state, including
  // signed off).
  const isAdmin = currentUser?.role === 'admin';
  const isCreator = !!currentUser && currentUser.id === instance?.created_by;
  const isLocked = currentStatus === 'signed_off' && !(isAdmin || isCreator);
  const canRevert = currentStatus !== 'draft' && (isAdmin || isCreator);

  const isOnline = useOnlineStatus();
  const pendingSync = isLocalId(instance?.id);

  // Keep every opened checklist on the device so it can be filled in
  // later with no signal. Cheap enough to do on open.
  useEffect(() => {
    if (!isOnline || !instance?.id || !template) return;
    cacheChecklist(instance, template).catch(() => { /* storage unavailable */ });
    // eslint-disable-next-line
  }, [instance?.id, template?.id, isOnline]);

  // Header fields the template marks required but which are still blank.
  // Drives the pre-sign-off reminder — a nudge, never a blocker, unless
  // the template is strict (then the backend refuses too, except for
  // admins and the checklist's own creator).
  const missingRequiredHeaders = (template?.header_fields || []).filter(
    (f) => f.required && !String(headerData[f.key] || '').trim(),
  );
  // Templates can insist on an equipment link — a weekly plant check that
  // doesn't say which machine is worthless as a record.
  const assetLinkMode = template?.asset_link || 'optional';
  const assetMissing = assetLinkMode === 'required' && !assetId;
  const signOffBlocked =
    !!template?.strict_header_fields
    && (missingRequiredHeaders.length > 0 || assetMissing)
    && !(isAdmin || isCreator);

  // Autosave — drafts / in-progress only, 1.5 s after typing stops.
  const autosaveState = useAutosave(
    { headerData, sectionsData, notes, projectId, assetId },
    () => handleSave(null, { silent: true }),
    { enabled: !isLocked && (currentStatus === 'draft' || currentStatus === 'in_progress') },
  );

  const handleRevertToDraft = async () => {
    if (!window.confirm(t('auto.iso.revertThisChecklistToDraftAll'))) return;
    setIsSaving(true);
    try {
      // Clear sign-offs
      const clearedSignOffs = signOffs.map(so => ({ ...so, signed: false, signed_at: null, signed_by_user_id: null, name: null }));
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instance.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: 'draft', sign_offs: clearedSignOffs }),
      });
      if (!resp.ok) throw new Error('Failed to revert');
      setCurrentStatus('draft');
      setSignOffs(clearedSignOffs);
      toast.success(t('auto.iso.revertedToDraftReadyForEditing'));
      setReloadHistory(k => k + 1);
    } catch (e) { toast.error(e.message); }
    finally { setIsSaving(false); }
  };

  const handleExportPDFLocal = async (mono = false, withPhotos = false) => {
    toast.info(withPhotos ? 'Generating PDF with photos…' : (mono ? t('auto.iso.generatingBWPdf') : t('auto.iso.generatingPdf')));
    try {
      const params = new URLSearchParams();
      if (mono) params.append('mono', 'true');
      if (withPhotos) params.append('with_photos', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const url = `${API_BASE}/api/checklists/instances/${instance.id}/pdf${qs}`;
      const resp = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `${instance.checklist_number || 'checklist'}_${instance.doc_ref || ''}${mono ? '_BW' : ''}${withPhotos ? '_with_photos' : ''}.pdf`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = weekFileName(filename);
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(t('auto.iso.pdfDownloaded'));
    } catch (e) { toast.error(e.message); }
  };

  if (!template) {
    return <div className="text-center py-8 text-gray-500">{t('auto.iso.loadingTemplate')}</div>;
  }

  const getSectionProgress = (sectionIdx) => {
    const section = sectionsData[sectionIdx];
    const tplSection = template.sections[sectionIdx];
    if (!tplSection) return { filled: 0, total: 0 };
    // Section types that don't have per-item ticks report progress
    // as 1/1 when any data is present.
    if (tplSection.type === 'equipment_status') {
      return { filled: section?.equipment_status ? 1 : 0, total: 1 };
    }
    if (tplSection.type === 'defect_log') {
      const rows = section?.defects || [];
      const nonEmpty = rows.filter(r => r.date || r.defect || r.action || r.item_ref).length;
      return { filled: nonEmpty, total: Math.max(rows.length, tplSection.min_rows || 1) };
    }
    if (!section) return { filled: 0, total: tplSection.items?.length || 0 };
    const total = tplSection.items.length;
    const filled = section.items?.filter(i => i.status || i.value || i.notes).length || 0;
    return { filled, total };
  };

  const DisciplineIcon = instance.discipline === 'electrical' ? Zap : Wrench;
  const approval = getApprovalStatus();
  const currentStatusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.draft;
  const signOffSummary = {
    signed: signOffs.filter(s => s.signed).length,
    total: signOffs.length,
  };

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden" data-testid="checklist-fill-form">
      {/* Top bar */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 sm:gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="checklist-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('auto.iso.back')}
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
              <DisciplineIcon className="h-4 w-4" />
              {instance.checklist_number && (
                <span className="font-mono text-sm font-semibold">{instance.checklist_number}</span>
              )}
              <span className="font-mono text-xs text-gray-400">{instance.doc_ref}</span>
              <Badge variant="outline" className="capitalize">{instance.discipline}</Badge>
              <Badge className={currentStatusCfg.color}>{currentStatusCfg.label}</Badge>
              {currentStatus === 'signed_off' && (
                <Badge
                  className="text-xs font-normal bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1"
                  data-testid="lock-badge"
                  title={isAdmin ? t('auto.iso.signedOffAdminOverrideAvailable') : t('auto.iso.lockedOnlyAdminsCanReOpen')}
                >
                  <Lock className="h-3 w-3" /> {isAdmin ? t('auto.iso.lockedAdmin') : t('auto.iso.locked')}
                </Badge>
              )}
            </div>
            <h2 className="font-semibold text-base sm:text-lg">{instance.template_name}</h2>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Revert to Draft button for admin/owner */}
          {canRevert && currentStatus !== 'draft' && (
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-red-600" onClick={handleRevertToDraft} disabled={isSaving} data-testid="revert-draft-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> {t('auto.iso.revertToDraft')}
            </Button>
          )}
          {/* Export PDF — color or print-friendly B&W */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> {t('auto.iso.pdf')} <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1"><DocLangToggle testId="doc-lang-checklistfillform-0" /></div>
              <DropdownMenuItem
                onClick={() => (onExportPDF ? onExportPDF(false) : handleExportPDFLocal(false, false))}
                data-testid="export-pdf-color"
              >
                <Download className="h-4 w-4 mr-2" /> {t('auto.iso.colorPdf')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => (onExportPDF ? onExportPDF(true) : handleExportPDFLocal(true, false))}
                data-testid="export-pdf-bw"
              >
                <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPDFLocal(false, true)}
                data-testid="export-pdf-with-photos"
              >
                <Camera className="h-4 w-4 mr-2" /> {t('auto.iso.colorPhotoEvidence')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportPDFLocal(true, true)}
                data-testid="export-pdf-bw-with-photos"
              >
                <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => handleSave(null)} disabled={isSaving || isLocked} data-testid="save-progress-btn">
            <Save className="h-4 w-4 mr-1" /> {isSaving ? t('auto.iso.saving') : t('auto.iso.saveProgress')}
          </Button>
          {currentStatus === 'completed' ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700 px-3 py-1.5 text-sm">
                <CheckCircle className="h-4 w-4 mr-1 inline" /> {t('auto.iso.approved')}
              </Badge>
              {signOffSummary.total > 0 && signOffSummary.signed < signOffSummary.total && (
                <Badge className="bg-amber-50 text-amber-700 px-2 py-1 text-xs">
                  <FileSignature className="h-3 w-3 mr-1 inline" /> {signOffSummary.signed}/{signOffSummary.total} signed
                </Badge>
              )}
            </div>
          ) : currentStatus === 'conditional_approval' ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-100 text-orange-700 px-3 py-1.5 text-sm">
                <AlertTriangle className="h-4 w-4 mr-1 inline" /> {t('auto.iso.conditional')}
              </Badge>
              {approval.canApprove && (
                <Button size="sm" onClick={() => handleSave('completed')} disabled={isSaving} data-testid="mark-complete-btn">
                  <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
                </Button>
              )}
            </div>
          ) : currentStatus === 'signed_off' ? (
            <Badge className="bg-green-100 text-green-700 px-3 py-1.5 text-sm">
              <FileSignature className="h-4 w-4 mr-1 inline" /> {t('auto.iso.signedOff')}
            </Badge>
          ) : (
            <>
              {approval.canApprove ? (
                <Button onClick={() => handleSave('completed')} disabled={isSaving} data-testid="mark-complete-btn">
                  <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
                </Button>
              ) : approval.allFilled && approval.hasFails ? (
                <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => handleSave('conditional_approval')} disabled={isSaving} data-testid="conditional-approval-btn">
                  <AlertTriangle className="h-4 w-4 mr-1" /> {t('auto.iso.conditionalApproval')}
                </Button>
              ) : (
                <Button variant="outline" disabled title={t('auto.iso.allItemsMustBeFilledPass')}>
                  <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {isLocked && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm"
          data-testid="lock-notice"
        >
          <Lock className="h-4 w-4 shrink-0" />
          <div>
            <strong>{t('auto.iso.thisChecklistIsSignedOffAnd')}</strong>{' '}
            Only an administrator can re-open or amend it. You can still
            view it and export the PDF.
          </div>
        </div>
      )}

      {/* `min-w-0` is load-bearing: a fieldset defaults to
          min-inline-size:min-content, so without it the widest table
          inside stretches the whole form past a phone screen. */}
      <fieldset disabled={isLocked} className={`m-0 p-0 border-0 space-y-4 min-w-0 ${isLocked ? 'opacity-60' : ''}`}>
      {/* Header fields */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('auto.iso.projectInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">{t('auto.iso.linkedProject')}</label>
              <Select value={projectId || 'none'} onValueChange={(v) => handleProjectChange(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-0.5 h-8 text-sm"><SelectValue placeholder={t('auto.iso.linkToProject')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.iso.none')}</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {assetLinkMode !== 'none' && (
              <div>
                <label className="text-xs font-medium text-gray-500">
                  {t('auto.iso.equipmentAsset')}
                  {assetLinkMode === 'required' ? <span className="text-amber-600 ml-0.5">*</span> : null}
                </label>
                <Select value={assetId || 'none'} onValueChange={(v) => handleAssetChange(v === 'none' ? '' : v)}>
                  <SelectTrigger
                    className={`mt-0.5 h-8 text-sm ${assetMissing ? 'border-amber-400 bg-amber-50/60' : ''}`}
                    data-testid="checklist-asset-select">
                    <SelectValue placeholder={t('auto.iso.linkToAToolMachineOr')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('auto.iso.none')}</SelectItem>
                    {assetOptions.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.on_this_project ? '★ ' : ''}{[a.inventory_number, a.name].filter(Boolean).join(' — ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedAsset && (
                  <div className="mt-1 flex items-center gap-2">
                    {selectedAsset.lead_picture_id && (
                      <LazyAuthImage
                        src={`${API_BASE}/api/checklists/asset-photo/${selectedAsset.id}`}
                        alt={selectedAsset.name}
                        className="block h-10 w-10 shrink-0 overflow-hidden rounded border bg-gray-50"
                        testId="checklist-selected-asset-thumb"
                      />
                    )}
                    <span className="text-[11px] text-gray-500">
                      {[selectedAsset.manufacturer, selectedAsset.model_name, selectedAsset.serial_number]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </div>
                )}
              </div>
            )}
            {template.header_fields?.map(field => {
              const isMissing = !!field.required && !String(headerData[field.key] || '').trim();
              return (
                <div key={field.key}>
                  <label className="text-xs font-medium text-gray-500">
                    {field.label}
                    {field.required ? <span className="text-amber-600 ml-0.5">*</span> : null}
                  </label>
                  <Input
                    type={field.type === 'date' ? 'date' : 'text'}
                    value={headerData[field.key] || ''}
                    onChange={(e) => setHeaderData(prev => ({ ...prev, [field.key]: e.target.value  }))}
                    placeholder={field.placeholder || ''}
                    className={`mt-0.5 h-8 text-sm ${isMissing ? 'border-amber-400 bg-amber-50/60' : ''}`}
                    data-testid={`checklist-header-${field.key}`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {template.sections?.map((section, sIdx) => {
        const { filled, total } = getSectionProgress(sIdx);
        const isExpanded = expandedSections[sIdx];

        return (
          <Card key={sIdx} data-testid={`checklist-section-${sIdx}`} className="min-w-0">
            <CardHeader
              className="pb-2 cursor-pointer select-none"
              onClick={() => toggleSection(sIdx)}
            >
              <div className="flex items-start sm:items-center justify-between gap-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0 flex-1">
                  <CardTitle className="text-sm break-words">{sIdx + 1}. {section.title}</CardTitle>
                  {section.reference && (
                    <span className="text-xs text-gray-400 break-words">{section.reference}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-400">{filled}/{total}</span>
                  <div className="w-10 sm:w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${filled === total && total > 0 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${total ? (filled / total) * 100 : 0}%` }}
                    />
                  </div>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent className="pt-0 min-w-0">
                {/* Weekly grid (7 days × P/F/N/A) — used by
                    forklift / MEWP / telehandler / pallet-truck
                    pre-use inspection templates. Renders a compact
                    horizontally-scrollable table so touch users on
                    tablets still get all 21 tick cells per row. */}
                {/* Notes-only section — no input fields; renders
                    each item as a read-only bullet so the section
                    can carry safety / statutory instructions. */}
                {section.type === 'notes' ? (
                  <div className="text-sm text-gray-700 space-y-1" data-testid={`notes-only-section-${sIdx}`}>
                    {(section.items || []).map((item, iIdx) => (
                      <div key={iIdx} className="flex gap-2">
                        <span className="text-gray-400">•</span>
                        <div>
                          <span className="font-medium">{item.label}</span>
                          {item.hint && <span className="text-gray-500"> — {item.hint}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : section.type === 'defect_log' ? (
                  <DefectLogTable
                    sIdx={sIdx}
                    minRows={section.min_rows || 1}
                    defects={getDefects(sIdx)}
                    onUpdate={updateDefect}
                    onAdd={addDefectRow}
                    onRemove={removeDefectRow}
                    isLocked={isLocked}
                  />
                ) : section.type === 'equipment_status' ? (
                  <EquipmentStatusPicker
                    sIdx={sIdx}
                    value={getEquipmentStatus(sIdx)}
                    note={getEquipmentNote(sIdx)}
                    onChange={setEquipmentStatus}
                    onNoteChange={setEquipmentNote}
                    isLocked={isLocked}
                  />
                ) : section.type === 'weekly_grid' ? (
                  <ScrollX className="-mx-2 px-2" testId={`weekly-grid-section-${sIdx}`}>
                    <table className="text-xs border-collapse min-w-max">
                      <thead>
                        <tr>
                          <th rowSpan={2} className="border p-1 text-left bg-gray-50 sticky left-0 w-[7.5rem] min-w-[7.5rem] sm:w-auto sm:min-w-[240px] shadow-[inset_-1px_0_0_rgb(229_231_235)] z-10">{t('auto.iso.item')}</th>
                          {WEEK_DAYS.map(d => (
                            <th key={d} colSpan={3} className="border p-1 bg-gray-100 text-gray-700 uppercase tracking-wide">{d}</th>
                          ))}
                        </tr>
                        <tr>
                          {WEEK_DAYS.map(d => (
                            <React.Fragment key={d}>
                              <th className="border p-0 bg-green-50 w-14 min-w-[3.25rem] sm:w-8 sm:min-w-0">
                                <button
                                  type="button"
                                  onClick={() => bulkSetDay(sIdx, d, 'pass')}
                                  className="w-full h-11 sm:h-6 text-green-800 hover:bg-green-100 font-semibold"
                                  title={`Mark every item as PASS on ${d}`}
                                  data-testid={`wg-bulk-${sIdx}-${d}-pass`}
                                >P</button>
                              </th>
                              <th className="border p-0 bg-red-50 w-14 min-w-[3.25rem] sm:w-8 sm:min-w-0">
                                <button
                                  type="button"
                                  onClick={() => bulkSetDay(sIdx, d, 'fail')}
                                  className="w-full h-11 sm:h-6 text-red-800 hover:bg-red-100 font-semibold"
                                  title={`Mark every item as FAIL on ${d}`}
                                  data-testid={`wg-bulk-${sIdx}-${d}-fail`}
                                >F</button>
                              </th>
                              <th className="border p-0 bg-gray-100 w-14 min-w-[3.25rem] sm:w-10 sm:min-w-0">
                                <button
                                  type="button"
                                  onClick={() => bulkSetDay(sIdx, d, 'na')}
                                  className="w-full h-11 sm:h-6 text-gray-700 hover:bg-gray-200 font-semibold text-[10px]"
                                  title={`Mark every item as N/A on ${d}`}
                                  data-testid={`wg-bulk-${sIdx}-${d}-na`}
                                >N/A</button>
                              </th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let running = 0;  // numbering skips subheader / signature rows
                          return section.items?.map((item, iIdx) => {
                            const itype = (item.type || 'check').toLowerCase();

                            // Bold group label row spanning the full width.
                            if (itype === 'subheader') {
                              return (
                                <tr key={iIdx}>
                                  <td colSpan={1 + WEEK_DAYS.length * 3} className="border-0 bg-gray-100 text-gray-800 font-semibold text-xs px-2 py-1.5 border-l-4 border-l-red-800">
                                    {item.label}
                                  </td>
                                </tr>
                              );
                            }

                            // Per-weekday initials text fields.
                            if (itype === 'signature') {
                              return (
                                <tr key={iIdx} className="bg-amber-50/40">
                                  <td className="border p-1 text-left sticky left-0 w-[7.5rem] min-w-[7.5rem] sm:w-auto sm:min-w-[240px] bg-amber-50/40 italic text-gray-600 text-[11px] break-words whitespace-normal shadow-[inset_-1px_0_0_rgb(229_231_235)] z-10">
                                    {item.label || 'Inspector initials'}
                                  </td>
                                  {WEEK_DAYS.map(d => (
                                    <td key={d} colSpan={3} className="border p-0.5">
                                      <input
                                        type="text"
                                        maxLength={6}
                                        value={getItemInitial(sIdx, iIdx, d)}
                                        onChange={(e) => updateItemInitials(sIdx, iIdx, d, e.target.value.toUpperCase())}
                                        placeholder={d}
                                        className="w-full h-10 sm:h-6 text-center text-[10px] font-semibold uppercase border border-gray-200 rounded bg-white focus:outline-none focus:border-red-500"
                                        data-testid={`wg-sig-${sIdx}-${iIdx}-${d}`}
                                        disabled={isLocked}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              );
                            }

                            // Regular check row.
                            running += 1;
                            return (
                              <tr key={iIdx}>
                                <td className="border p-1 text-left bg-white sticky left-0 w-[7.5rem] min-w-[7.5rem] sm:w-auto sm:min-w-[240px] shadow-[inset_-1px_0_0_rgb(229_231_235)] z-10 align-top">
                                  <div className="font-medium break-words whitespace-normal leading-snug">{running}. {item.label}</div>
                                  {item.hint && <div className="text-[10px] text-gray-500 break-words whitespace-normal">{item.hint}</div>}
                                </td>
                                {WEEK_DAYS.map(d => {
                                  const cur = getItemDay(sIdx, iIdx, d);
                                  const cell = (val, label, cls, active) => (
                                  <td className="border p-0.5">
                                      <button
                                        type="button"
                                        onClick={() => updateItemDay(sIdx, iIdx, d, val)}
                                        data-testid={`wg-${sIdx}-${iIdx}-${d}-${val}`}
                                        className={`w-full h-11 sm:h-6 flex items-center justify-center rounded text-xs sm:text-[10px] font-semibold ${
                                          active ? cls : 'hover:bg-gray-100 text-gray-400'
                                        }`}
                                      >
                                        {active ? '✕' : label}
                                      </button>
                                    </td>
                                  );
                                  return (
                                    <React.Fragment key={d}>
                                      {cell('pass', 'P', 'bg-green-600 text-white', cur === 'pass')}
                                      {cell('fail', 'F', 'bg-red-600 text-white', cur === 'fail')}
                                      {cell('na',   '·', 'bg-gray-500 text-white', cur === 'na')}
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                    <div className="text-[11px] text-gray-500 mt-2">
                      Tap a cell to mark the day (tap again to clear).
                      Click a <strong>P</strong> / <strong>F</strong> / <strong>N/A</strong> header to bulk-fill that day&apos;s column for every item in this section; click the same header again to clear the column.
                    </div>
                  </ScrollX>
                ) : (
                <>
                {/* Section-level bulk actions (only for sections with check/test items) */}
                {section.type !== 'text' && (
                  <div className="flex flex-wrap items-center gap-1 mb-3 pb-2 border-b border-gray-100">
                    <span className="text-xs text-gray-400 mr-2">{t('auto.iso.setAll')}</span>
                    {STATUS_BUTTONS.map(btn => {
                      const BtnIcon = btn.icon;
                      return (
                        <button
                          key={btn.value}
                          className={`px-2 py-1 flex items-center gap-1 rounded border text-xs transition-all ${btn.color}`}
                          onClick={(e) => { e.stopPropagation(); setSectionStatus(sIdx, btn.value); }}
                          data-testid={`section-${sIdx}-set-${btn.value}`}
                        >
                          <BtnIcon className="h-3 w-3" /> {btn.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="space-y-2">
                  {section.items?.map((item, iIdx) => {
                    const data = getItemData(sIdx, iIdx);

                    if (item.type === 'subheader') {
                      return (
                        <div key={iIdx} className="mt-2 mb-1 px-3 py-1.5 bg-gray-100 border-l-4 border-red-700 text-sm font-semibold text-gray-800">
                          {item.label}
                        </div>
                      );
                    }

                    if (item.type === 'text') {
                      return (
                        <div key={iIdx} className="py-2 border-b border-gray-50 last:border-0">
                          <label className="text-sm font-medium">{item.label}</label>
                          {item.hint && <p className="text-xs text-gray-400">{item.hint}</p>}
                          <textarea
                            className="w-full mt-1 p-2 border rounded text-sm min-h-[60px] resize-y"
                            value={data.notes || ''}
                            onChange={(e) => updateItem(sIdx, iIdx, 'notes', e.target.value)}
                            placeholder={t('auto.iso.enterDetails')}
                          />
                        </div>
                      );
                    }

                    // check or test type
                    return (
                      <div key={iIdx} className="py-2 border-b border-gray-50 last:border-0">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                          {/* Status toggles */}
                          <div className="flex gap-1 flex-shrink-0 sm:pt-0.5 order-1 sm:order-none">
                            {STATUS_BUTTONS.map(btn => {
                              const BtnIcon = btn.icon;
                              const isActive = data.status === btn.value;
                              return (
                                <button
                                  key={btn.value}
                                  className={`w-7 h-7 flex items-center justify-center rounded border text-xs transition-all ${isActive ? btn.color + ' font-bold ring-1' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
                                  onClick={() => updateItem(sIdx, iIdx, 'status', btn.value)}
                                  title={btn.label}
                                  data-testid={`item-${sIdx}-${iIdx}-${btn.value}`}
                                >
                                  <BtnIcon className="h-3.5 w-3.5" />
                                </button>
                              );
                            })}
                          </div>
                          {/* Label & hint */}
                          <div className="flex-1 min-w-0 order-2 sm:order-none">
                            <p className="text-sm break-words">{item.label}</p>
                            {item.hint && <p className="text-xs text-gray-400 break-words">{item.hint}</p>}
                          </div>
                          {/* Value field for test items */}
                          {item.type === 'test' && (
                            <Input
                              className="w-full sm:w-28 h-7 text-xs sm:flex-shrink-0 order-3 sm:order-none"
                              placeholder={t('auto.iso.value')}
                              value={data.value || ''}
                              onChange={(e) => updateItem(sIdx, iIdx, 'value', e.target.value)}
                              data-testid={`item-${sIdx}-${iIdx}-value`}
                            />
                          )}
                          {/* Notes — roomy on desktop, collapses to a
                              full-width line underneath on mobile. */}
                          <Input
                            className="w-full sm:flex-[2] sm:min-w-[14rem] h-7 text-xs order-4 sm:order-none"
                            placeholder={t('auto.iso.notes')}
                            value={data.notes || ''}
                            onChange={(e) => updateItem(sIdx, iIdx, 'notes', e.target.value)}
                            data-testid={`item-${sIdx}-${iIdx}-notes`}
                          />
                        </div>
                        {/* Photo gallery — per checklist item */}
                        {instance?.id && (
                          <div className="mt-2 sm:ml-[calc(3*1.75rem+0.75rem)]">
                            <EntityPhotoGallery
                              entityType="checklist"
                              entityId={instance.id}
                              itemKey={`${sIdx}.${iIdx}`}
                              readOnly={isLocked}
                              compact
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* General Notes */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('auto.iso.generalNotes')}</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            className="w-full p-2 border rounded text-sm min-h-[120px] lg:min-h-[180px] resize-y"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('auto.iso.additionalObservationsOrNotes')}
            data-testid="checklist-general-notes"
          />
        </CardContent>
      </Card>
      </fieldset>

      {/* Sign-Off */}
      {(!isOnline || pendingSync) && (
        <div
          className="flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700"
          data-testid="checklist-offline-banner"
        >
          <CloudOff className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>{isOnline ? t('auto.iso.waitingToSync') : t('auto.iso.workingOffline')}</strong>
            <div className="text-xs mt-0.5 opacity-80">
              {isOnline
                ? 'This sheet was filled in offline and is queued — it will upload shortly.'
                : 'Keep filling this in as normal. Everything saves to this device and uploads by itself as soon as you have signal. You can sign off too — signatures upload with everything else. PDF export needs a connection.'}
            </div>
          </div>
        </div>
      )}
      {missingRequiredHeaders.length > 0 && (
        <div
          className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
            signOffBlocked
              ? 'border-red-300 bg-red-50 text-red-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}
          data-testid="checklist-required-header-reminder"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>
              {signOffBlocked ? t('auto.iso.signOffIsBlocked') : t('auto.iso.beforeYouSignOff')}
            </strong>{' '}
            {missingRequiredHeaders.length === 1 ? t('auto.iso.thisFieldIs') : t('auto.iso.theseFieldsAre')}{' '}
            still empty in Project Information —{' '}
            <span className="font-medium">{missingRequiredHeaders.map((f) => f.label).join(', ')}</span>.
            <div className="text-xs mt-0.5 opacity-80">
              {signOffBlocked
                ? 'This template requires them. Fill them in and save, or ask an administrator to sign off.'
                : 'Filling these in means this checklist can be traced back to the right piece of kit. You can still sign off without them.'}
            </div>
          </div>
        </div>
      )}
      <Card data-testid="sign-off-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSignature className="h-4 w-4" />
            {t('auto.iso.signOff')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {signOffs.map((so, idx) => {
              const placeholder = defaultSignName(so.role);
              const inputVal = so.name ?? (placeholder || '');
              return (
                <div key={idx} className="p-3 border rounded-lg">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0 sm:min-w-[220px] w-full sm:w-auto">
                      <p className="text-sm font-medium">{so.role}</p>
                      {so.signed ? (
                        <div className="flex flex-col gap-1 mt-1">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-700">{so.name}</span>
                            {so.pending_sync && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
                                data-testid={`sign-off-pending-sync-${idx}`}
                              >
                                <CloudOff className="h-3 w-3" /> {t('auto.iso.pendingSync')}
                              </span>
                            )}
                          </div>
                          {so.signed_at && (
                            <div className="text-xs text-gray-500" data-testid={`signed-at-${idx}`}>
                              {formatSignedLocal(so)}
                              <span className="block text-[10px] text-gray-400">{formatSignedUtc(so)}</span>
                            </div>
                          )}
                          {so.signature_image && (
                            <img
                              src={so.signature_image}
                              alt={`${so.role} signature`}
                              className="max-h-16 border rounded bg-white p-1 self-start"
                              data-testid={`signed-signature-img-${idx}`}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1">
                          <Input
                            className="h-11 sm:h-8 text-base sm:text-sm w-full sm:max-w-[200px]"
                            placeholder={placeholder || 'Enter name'}
                            value={inputVal}
                            onChange={(e) => {
                              setSignOffs(prev => prev.map((s, i) => i === idx ? { ...s, name: e.target.value } : s));
                            }}
                            data-testid={`sign-off-name-${idx}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full sm:w-auto h-11 sm:h-8 text-base sm:text-sm"
                            onClick={() => handleSignOff(idx)}
                            disabled={!inputVal || !inputVal.trim() || signOffBlocked}
                            title={
                              signOffBlocked ? 'Fill the required header fields first'
                                : !isOnline ? 'Signs on this device and uploads when you have signal'
                                : undefined
                            }
                            data-testid={`sign-off-btn-${idx}`}
                          >
                            <FileSignature className="h-3 w-3 mr-1" /> {t('auto.iso.sign')}
                          </Button>
                        </div>
                      )}
                    </div>
                    {!so.signed && (
                      <div className="w-full sm:w-[280px]">
                        <SignaturePad
                          value={so.signature_image || null}
                          onChange={(dataUrl) => updateSignatureImage(idx, dataUrl)}
                          height={100}
                          testIdPrefix={`sign-pad-${idx}`}
                          savedSignature={savedSignature}
                          onSaveDefault={async (dataUrl) => {
                            const ok = await saveAsDefault(dataUrl);
                            toast[ok ? 'success' : 'error'](ok ? t('auto.iso.savedAsYourDefaultSignature') : t('auto.iso.failedToSaveDefault'));
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Bar */}
      <div className="flex justify-between items-center py-2">
        <Button variant="ghost" onClick={onBack} data-testid="btn-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('auto.iso.backToList')}
        </Button>
        <div className="flex gap-2">
          <span className="self-center text-xs text-slate-500 min-w-[70px]" data-testid="autosave-indicator">
            {AUTOSAVE_LABEL[autosaveState]}
          </span>
          <Button variant="outline" onClick={() => handleSave(null)} disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" /> {t('auto.iso.saveProgress')}
          </Button>
          {currentStatus === 'draft' || currentStatus === 'in_progress' ? (
            <>
              {approval.canApprove && (
                <Button onClick={() => handleSave('completed')} disabled={isSaving}>
                  <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
                </Button>
              )}
              {approval.allFilled && approval.hasFails && (
                <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => handleSave('conditional_approval')} disabled={isSaving}>
                  <AlertTriangle className="h-4 w-4 mr-1" /> {t('auto.iso.conditionalApproval')}
                </Button>
              )}
            </>
          ) : currentStatus === 'conditional_approval' && approval.canApprove ? (
            <Button onClick={() => handleSave('completed')} disabled={isSaving}>
              <CheckCircle className="h-4 w-4 mr-1" /> {t('auto.iso.approve')}
            </Button>
          ) : null}
        </div>
      </div>

      {instance?.id && (
        <AuditTrailPanel kind="checklist" instanceId={instance.id} reloadKey={reloadHistory} />
      )}

      <FloatingActions actions={[
        { icon: Save, label: isSaving ? t('auto.iso.saving2') : t('auto.iso.saveProgress2'), tone: 'primary',
          testId: 'checklist-floating-save-btn', disabled: isSaving || isLocked,
          onClick: () => handleSave(null) },
        { icon: CheckCircle, label: t('auto.lbl.approve'), tone: 'success',
          testId: 'checklist-floating-approve-btn',
          hidden: !(approval.canApprove
            && ['draft', 'in_progress', 'conditional_approval'].includes(currentStatus)),
          disabled: isSaving, onClick: () => handleSave('completed') },
        { icon: Download, label: t('auto.lbl.exportPdf'), testId: 'checklist-floating-pdf-btn',
          onClick: () => (onExportPDF ? onExportPDF(false) : handleExportPDFLocal(false, false)) },
      ]} />
    </div>
  );
};

// ── Defect Log editable table ─────────────────────────────────────
// Pen-fillable grid for recording faults spotted during the week.
// Columns are fixed so the printed sheet matches the reference form.
const DefectLogTable = ({ sIdx, minRows, defects, onUpdate, onAdd, onRemove, isLocked }) => {
  const { t } = useTranslation();
  const rows = Math.max(defects.length, minRows);
  const padded = [...defects];
  while (padded.length < rows) padded.push({});
  return (
    <div className="overflow-x-auto -mx-2 px-2" data-testid={`defect-log-${sIdx}`}>
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr className="bg-gray-100 text-gray-700 uppercase text-[10px]">
            <th className="border p-1 w-8">#</th>
            <th className="border p-1 w-24">{t('auto.iso.date')}</th>
            <th className="border p-1 w-16">{t('auto.iso.itemRef')}</th>
            <th className="border p-1">{t('auto.iso.defectDescription')}</th>
            <th className="border p-1 w-14">{t('auto.iso.risk')}</th>
            <th className="border p-1">{t('auto.iso.actionTaken')}</th>
            <th className="border p-1 w-20">{t('auto.iso.closedBy')}</th>
            <th className="border p-1 w-24">{t('auto.iso.closedDate')}</th>
            <th className="border p-1 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {padded.map((d, rIdx) => (
            <tr key={rIdx}>
              <td className="border text-center text-gray-400 text-[10px]">{rIdx + 1}</td>
              <td className="border p-0">
                <span className="relative block">
                  <input type="date" value={d.date || ''} disabled={isLocked}
                    onChange={(e) => onUpdate(sIdx, rIdx, 'date', e.target.value)}
                    className={`w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white peer ${dateTextHiddenCls}`}
                    data-testid={`dl-${sIdx}-${rIdx}-date`} />
                  <DateOverlay value={d.date || ''} disabled={isLocked} className="px-1 text-xs" />
                </span>
              </td>
              <td className="border p-0">
                <input type="text" value={d.item_ref || ''} disabled={isLocked}
                  onChange={(e) => onUpdate(sIdx, rIdx, 'item_ref', e.target.value)}
                  className="w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white"
                  data-testid={`dl-${sIdx}-${rIdx}-item_ref`} />
              </td>
              <td className="border p-0">
                <input type="text" value={d.defect || ''} disabled={isLocked}
                  onChange={(e) => onUpdate(sIdx, rIdx, 'defect', e.target.value)}
                  className="w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white"
                  data-testid={`dl-${sIdx}-${rIdx}-defect`} />
              </td>
              <td className="border p-0">
                <select value={d.risk || ''} disabled={isLocked}
                  onChange={(e) => onUpdate(sIdx, rIdx, 'risk', e.target.value)}
                  className={`w-full h-7 text-xs text-center bg-transparent focus:outline-none focus:bg-white font-semibold ${
                    d.risk === 'H' ? 'text-red-700' : d.risk === 'M' ? 'text-amber-700' : d.risk === 'L' ? 'text-green-700' : 'text-gray-400'
                  }`}
                  data-testid={`dl-${sIdx}-${rIdx}-risk`}>
                  <option value=""></option>
                  <option value="L">L</option>
                  <option value="M">M</option>
                  <option value="H">H</option>
                </select>
              </td>
              <td className="border p-0">
                <input type="text" value={d.action || ''} disabled={isLocked}
                  onChange={(e) => onUpdate(sIdx, rIdx, 'action', e.target.value)}
                  className="w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white"
                  data-testid={`dl-${sIdx}-${rIdx}-action`} />
              </td>
              <td className="border p-0">
                <input type="text" value={d.closed_by || ''} disabled={isLocked}
                  onChange={(e) => onUpdate(sIdx, rIdx, 'closed_by', e.target.value)}
                  className="w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white"
                  data-testid={`dl-${sIdx}-${rIdx}-closed_by`} />
              </td>
              <td className="border p-0">
                <span className="relative block">
                  <input type="date" value={d.closed_date || ''} disabled={isLocked}
                    onChange={(e) => onUpdate(sIdx, rIdx, 'closed_date', e.target.value)}
                    className={`w-full h-7 px-1 text-xs bg-transparent focus:outline-none focus:bg-white peer ${dateTextHiddenCls}`}
                    data-testid={`dl-${sIdx}-${rIdx}-closed_date`} />
                  <DateOverlay value={d.closed_date || ''} disabled={isLocked} className="px-1 text-xs" />
                </span>
              </td>
              <td className="border p-0 text-center">
                {rIdx >= minRows && !isLocked && (
                  <button
                    type="button"
                    onClick={() => onRemove(sIdx, rIdx)}
                    className="text-red-300 hover:text-red-600 text-xs px-1"
                    title={t('auto.iso.removeRow')}
                    data-testid={`dl-${sIdx}-${rIdx}-remove`}
                  >×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isLocked && (
        <button
          type="button"
          onClick={() => onAdd(sIdx)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-800 px-2 py-1"
          data-testid={`dl-${sIdx}-add`}
        >{t('auto.iso.addDefectRow')}</button>
      )}
    </div>
  );
};

// ── Equipment Status radio picker ────────────────────────────────
// Three-option single-choice with an optional free-text note.
const EquipmentStatusPicker = ({ sIdx, value, note, onChange, onNoteChange, isLocked }) => {
  const { t } = useTranslation();
  const options = [
    { key: 'in_service', label: t('auto.lbl.inService'), color: 'border-green-500 bg-green-50 text-green-800' },
    { key: 'restricted', label: t('auto.lbl.restrictedUse'), color: 'border-amber-500 bg-amber-50 text-amber-800' },
    { key: 'removed', label: t('auto.lbl.removedFromService'), color: 'border-red-500 bg-red-50 text-red-800' },
  ];
  return (
    <div className="space-y-3" data-testid={`equipment-status-${sIdx}`}>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => !isLocked && onChange(sIdx, active ? '' : opt.key)}
              disabled={isLocked}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                active ? opt.color + ' font-bold ring-1 ring-offset-1' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
              data-testid={`es-${sIdx}-${opt.key}`}
            >
              <span className="mr-1.5">{active ? '●' : '○'}</span>
              {opt.label}
            </button>
          );
        })}
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500">{t('auto.iso.noteOptional')}</label>
        <input
          type="text"
          value={note || ''}
          disabled={isLocked}
          onChange={(e) => onNoteChange(sIdx, e.target.value)}
          placeholder="e.g. restricted to indoor use until forks replaced"
          className="w-full mt-0.5 h-8 px-2 text-sm border rounded focus:outline-none focus:border-gray-400"
          data-testid={`es-${sIdx}-note`}
        />
      </div>
    </div>
  );
};

export default ChecklistFillForm;
